/**
 * oRPC Client — 全 Contract を統合した単一クライアント
 *
 * 新しい API を追加する場合:
 * 1. Contract を定義（共有パッケージ or ローカル）
 * 2. appContract に追加
 * 3. client.xxx.method() で呼び出し
 */
import { createORPCClient } from '@orpc/client';
import { oc, type ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { appIntegrationsForSelectContract, chatContract, contractAlertContract, customerLedgerContract, inboundDataPipelineContract, inboundDataPipelineEnumsContract, dealLedgerContract, findPersonHistoriesFromEventStoreContract, storageSourceTypesForSelectContract, individualEquipmentContract, listDeviceCostByMemberContract, listDeviceCostByMemberForDeviceCatalogContract, listDevicePaymentByMemberContract, listDevicePaymentByMemberForDeviceCatalogContract, listSoftwareCostByMemberContract, listSoftwareCostByMemberForSoftwareContract, listSoftwarePaymentByMemberContract, listSoftwarePaymentByMemberForSoftwareContract, listPeopleOperationActivityContract, listPeopleOperationActivityTypesContract, oauth2Contract, openidMetadataContract, outboundDataPipelineConfigContract, outboundDataPipelineTaskContract, peopleCsvContract, peopleCsvImportJobContract, s3Contract, softwareContract, ssoIntegrationsContract, summaryPeopleOperationActivityContract, supplierLedgerContract, supplierLedgerSettingsContract, workModeContract, loginEmailPolicyContract, amendEgovApplicationByStepInstanceContract, bulkSubmitEgovApplicationsByStepInstancesContract, bulkSyncEgovApplicationsByStepInstancesContract, listEgovApplicationStepsByTagContract, submitEgovApplicationByStepInstanceContract, syncEgovApplicationStatusContract, withdrawEgovApplicationByStepInstanceContract, equipmentListContract, listDeviceCostByDepartmentContract, listDeviceCostByDepartmentForDeviceCatalogContract, listDevicePaymentByDepartmentContract, listDevicePaymentByDepartmentForDeviceCatalogContract, listSoftwareCostByDepartmentContract, listSoftwareCostByDepartmentForSoftwareContract, listSoftwarePaymentByDepartmentContract, listSoftwarePaymentByDepartmentForSoftwareContract } from '@standandforce/api-contract';
import { authFetch, getBaseUrl } from './auth-fetch';
// TODO(F-1): `@standandforce/api-contract@0.0.1062` が install 可能になったら
// ローカル Contract を廃し、`accessesAiContract` を `@standandforce/api-contract` から import する。
import { accessesAiContract } from './contracts/accesses-ai';
import { getAppIntegrationGwsConnectionRoute, patchAppIntegrationGwsConnectionRoute } from './contracts/app-integration-gws-connection';
// TODO: `@standandforce/api-contract` に content 対応版が publish されたら差し替える
import { boePackageNotificationConfigContract } from './contracts/boe-package-notification-config';
import { businessPartnerOverviewContract } from './contracts/business-partner-overview';
import { contractLedgerContract } from './contracts/contract-ledger';
import { contractOverviewContract } from './contracts/contract-overview';
import { inboundDataPipelineCsvContract } from './contracts/data-pipeline-csv';
import { deviceGroupContract } from './contracts/device-group';
import { govProfileSubmittableContract } from './contracts/gov-profile-submittable';
import { itemLedgerContract } from './contracts/item-ledger';
import { laborCoreYearEndAdjustmentInstancesContract } from './contracts/labor-core-year-end-adjustment-instances';
import { loginMethodPolicyContract } from './contracts/login-method-policy';
import { notificationAndResponsePackageAssignmentsContract, notificationAndResponsePoliciesContract, notificationAndResponseRecipientOptionsContract, notificationAndResponseVariablesContract } from './contracts/notification-and-response-policies';
import { placeOverviewContract } from './contracts/place-overview';

/**
 * 全 Contract を 1 つの Router にマージ。
 * ファイルアップロード用ローカル Contract と共有パッケージ Contract を統合。
 */
export const appContract = oc.router({
  workMode: workModeContract,
  s3: s3Contract,
  peopleCsv: peopleCsvContract,
  peopleCsvImportJob: peopleCsvImportJobContract,
  inboundDataPipelineCsv: inboundDataPipelineCsvContract,
  inboundDataPipeline: inboundDataPipelineContract,
  inboundDataPipelineEnums: inboundDataPipelineEnumsContract,
  outboundDataPipeline: outboundDataPipelineConfigContract,
  outboundDataPipelineTask: outboundDataPipelineTaskContract,
  appIntegrationsForSelect: appIntegrationsForSelectContract,
  storageSourceTypesForSelect: storageSourceTypesForSelectContract,
  contractAlert: contractAlertContract,
  contractLedger: contractLedgerContract,
  contractOverview: contractOverviewContract,
  loginEmailPolicy: loginEmailPolicyContract,
  loginMethodPolicy: loginMethodPolicyContract,
  software: softwareContract,
  accessesAi: accessesAiContract,
  appIntegration: oc.router({
    gwsConnection: patchAppIntegrationGwsConnectionRoute,
    getGwsConnection: getAppIntegrationGwsConnectionRoute
  }),
  individualEquipment: individualEquipmentContract,
  equipmentList: equipmentListContract,
  deviceCost: oc.router({
    byMember: listDeviceCostByMemberContract,
    byDepartment: listDeviceCostByDepartmentContract,
    byMemberForDeviceCatalog: listDeviceCostByMemberForDeviceCatalogContract,
    byDepartmentForDeviceCatalog: listDeviceCostByDepartmentForDeviceCatalogContract
  }),
  devicePayment: oc.router({
    byMember: listDevicePaymentByMemberContract,
    byDepartment: listDevicePaymentByDepartmentContract,
    byMemberForDeviceCatalog: listDevicePaymentByMemberForDeviceCatalogContract,
    byDepartmentForDeviceCatalog: listDevicePaymentByDepartmentForDeviceCatalogContract
  }),
  softwareCost: oc.router({
    byMember: listSoftwareCostByMemberContract,
    byDepartment: listSoftwareCostByDepartmentContract,
    byMemberForSoftware: listSoftwareCostByMemberForSoftwareContract,
    byDepartmentForSoftware: listSoftwareCostByDepartmentForSoftwareContract
  }),
  softwarePayment: oc.router({
    byMember: listSoftwarePaymentByMemberContract,
    byDepartment: listSoftwarePaymentByDepartmentContract,
    byMemberForSoftware: listSoftwarePaymentByMemberForSoftwareContract,
    byDepartmentForSoftware: listSoftwarePaymentByDepartmentForSoftwareContract
  }),
  listPeopleOperationActivity: listPeopleOperationActivityContract,
  listPeopleOperationActivityTypes: listPeopleOperationActivityTypesContract,
  placeOverview: placeOverviewContract,
  businessPartnerOverview: businessPartnerOverviewContract,
  summaryPeopleOperationActivity: summaryPeopleOperationActivityContract,
  dealLedger: dealLedgerContract,
  customerLedger: {
    customersList: customerLedgerContract.customersList,
    customersCounts: customerLedgerContract.customersCounts,
    customers: {
      getProfileHeader: customerLedgerContract.customerProfileHeader,
      getDetail: customerLedgerContract.customerDetail,
      update: customerLedgerContract.customerUpdate,
      commercialTerms: {
        create: customerLedgerContract.commercialTermsCreate,
        update: customerLedgerContract.commercialTermsUpdate,
        delete: customerLedgerContract.commercialTermsDelete
      }
    }
  },
  itemLedger: {
    itemsList: itemLedgerContract.itemsList,
    itemsDetail: itemLedgerContract.itemsDetail,
    itemsCreate: itemLedgerContract.itemsCreate,
    itemsUpdate: itemLedgerContract.itemsUpdate,
    itemsChangeStatus: itemLedgerContract.itemsChangeStatus
  },
  supplierLedger: {
    suppliersList: supplierLedgerContract.suppliersList,
    suppliersCounts: supplierLedgerContract.suppliersCounts,
    suppliers: {
      getProfileHeader: supplierLedgerContract.supplierProfileHeader,
      getDetail: supplierLedgerContract.supplierDetail,
      update: supplierLedgerContract.supplierUpdate,
      avlEntries: {
        create: supplierLedgerContract.avlEntryCreate,
        update: supplierLedgerContract.avlEntryUpdate,
        delete: supplierLedgerContract.avlEntryDelete
      }
    },
    settings: supplierLedgerSettingsContract
  },
  laborCoreEgovApplicationSteps: oc.router({
    listByTag: listEgovApplicationStepsByTagContract
  }),
  laborCoreEgovBulk: oc.router({
    submitByStepInstances: bulkSubmitEgovApplicationsByStepInstancesContract,
    syncByStepInstances: bulkSyncEgovApplicationsByStepInstancesContract
  }),
  laborCoreEgovOperations: oc.router({
    amendByStepInstance: amendEgovApplicationByStepInstanceContract,
    withdrawByStepInstance: withdrawEgovApplicationByStepInstanceContract,
    syncApplication: syncEgovApplicationStatusContract
  }),
  laborCoreEgovSubmit: oc.router({
    submitByStepInstance: submitEgovApplicationByStepInstanceContract
  }),
  laborCoreYearEndAdjustmentInstances: laborCoreYearEndAdjustmentInstancesContract,
  govProfileSubmittable: govProfileSubmittableContract,
  personAggregateEvents: findPersonHistoriesFromEventStoreContract,
  aiOperatorChat: chatContract,
  oauth2: oauth2Contract,
  openidMetadata: openidMetadataContract,
  ssoIntegrations: ssoIntegrationsContract,
  boePackageNotificationConfig: boePackageNotificationConfigContract,
  notifResponsePolicies: notificationAndResponsePoliciesContract,
  notifResponsePackageAssignments: notificationAndResponsePackageAssignmentsContract,
  notifResponseRecipientOptions: notificationAndResponseRecipientOptionsContract,
  notifResponseVariables: notificationAndResponseVariablesContract,
  deviceGroup: deviceGroupContract
});
const link = new OpenAPILink(appContract, {
  url: getBaseUrl() ?? '',
  fetch: authFetch
});
export const orpcClient: ContractRouterClient<typeof appContract> = createORPCClient(link);